/**
 * PaymentSuccessPage - Handle payment completion with status polling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  XCircle,
  Clock,
  Download,
  ArrowRight,
  RefreshCw,
  Package,
  Home,
  AlertTriangle,
} from 'lucide-react';
import { getBackendUrl } from '../config/api';

const API_URL = getBackendUrl();

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, paid, unpaid, expired, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState(null);
  
  const maxPolls = 10;
  const pollInterval = 2000;
  
  // Poll payment status
  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }
      
      const data = await response.json();
      setPaymentDetails(data);
      
      if (data.payment_status === 'paid') {
        setStatus('paid');
        return true; // Stop polling
      } else if (data.status === 'expired') {
        setStatus('expired');
        return true; // Stop polling
      } else if (data.status === 'complete' && data.payment_status !== 'paid') {
        setStatus('unpaid');
        return true; // Stop polling
      }
      
      return false; // Continue polling
    } catch (err) {
      console.error('Payment status check error:', err);
      setError(err.message);
      setStatus('error');
      return true; // Stop polling on error
    }
  }, [sessionId]);
  
  // Polling effect
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found in URL');
      return;
    }
    
    let timeoutId;
    
    const poll = async () => {
      const shouldStop = await checkPaymentStatus();
      
      if (!shouldStop && pollCount < maxPolls) {
        setPollCount(prev => prev + 1);
        timeoutId = setTimeout(poll, pollInterval);
      } else if (pollCount >= maxPolls && status === 'checking') {
        setStatus('timeout');
      }
    };
    
    poll();
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, checkPaymentStatus, pollCount, status]);
  
  // Handle download after successful purchase
  const handleDownloadPreset = async () => {
    if (!paymentDetails?.metadata?.preset_id) {
      toast.error('Unable to download preset');
      return;
    }
    
    try {
      // The preset should now be accessible since payment is complete
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${paymentDetails.metadata.preset_id}/download`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('importedPreset', JSON.stringify(data));
        toast.success('Preset downloaded! Opening in studio...');
        navigate('/studio');
      } else {
        throw new Error('Download failed');
      }
    } catch (err) {
      toast.error('Failed to download preset');
      console.error(err);
    }
  };
  
  // Render based on status
  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-6">
              <RefreshCw className="w-10 h-10 text-violet-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-zinc-400 mb-4">Please wait while we confirm your payment...</p>
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
              <Clock className="w-4 h-4" />
              Checking status... ({pollCount}/{maxPolls})
            </div>
          </motion.div>
        );
        
      case 'paid':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-zinc-400 mb-6">
              Thank you for your purchase. Your preset is ready to download.
            </p>
            
            {/* Payment Details */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
              <div className="flex items-center justify-between py-2 border-b border-zinc-800">
                <span className="text-zinc-400">Amount</span>
                <span className="text-white font-semibold">
                  ${paymentDetails?.amount?.toFixed(2)} {paymentDetails?.currency?.toUpperCase()}
                </span>
              </div>
              {paymentDetails?.metadata?.preset_name && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-zinc-400">Preset</span>
                  <span className="text-white">{paymentDetails.metadata.preset_name}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {paymentDetails?.metadata?.preset_id && (
                <Button
                  onClick={handleDownloadPreset}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="download-purchased-preset"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Preset
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/marketplace')}
                className="border-zinc-700"
              >
                <Package className="w-4 h-4 mr-2" />
                Browse More Presets
              </Button>
            </div>
          </motion.div>
        );
        
      case 'unpaid':
      case 'expired':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {status === 'expired' ? 'Session Expired' : 'Payment Incomplete'}
            </h1>
            <p className="text-zinc-400 mb-6">
              {status === 'expired' 
                ? 'This payment session has expired. Please try again.'
                : 'The payment was not completed. Please try again if you wish to purchase.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/marketplace')}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Return to Marketplace
              </Button>
            </div>
          </motion.div>
        );
        
      case 'timeout':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Status Check Timeout</h1>
            <p className="text-zinc-400 mb-6">
              We couldn't confirm your payment status. If you completed the payment,
              it may take a few minutes to process.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => {
                  setPollCount(0);
                  setStatus('checking');
                }}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/marketplace')}
                className="border-zinc-700"
              >
                Return to Marketplace
              </Button>
            </div>
          </motion.div>
        );
        
      case 'error':
      default:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
            <p className="text-zinc-400 mb-2">
              {error || 'We encountered an error checking your payment status.'}
            </p>
            <p className="text-sm text-zinc-500 mb-6">
              If you completed the payment, please contact support with your session ID.
            </p>
            {sessionId && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 mb-6 max-w-sm mx-auto">
                <p className="text-xs text-zinc-500 mb-1">Session ID</p>
                <p className="text-sm text-zinc-300 font-mono break-all">{sessionId}</p>
              </div>
            )}
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-violet-600 hover:bg-violet-500"
            >
              <Home className="w-4 h-4 mr-2" />
              Return to Marketplace
            </Button>
          </motion.div>
        );
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {renderContent()}
      </div>
    </div>
  );
}
